import { useEffect, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'src/constants/QueryKeys'
import { DroplistBrands, DroplistConfig, DroplistProduct, DroplistRun } from 'src/types/Droplist'
import { retryServerErrors } from 'src/utils/apiReason'
import { droplistApi } from 'src/utils/droplistApi'

type RunResponse = { run: DroplistRun | null }

export const useDroplistBrands = () =>
    useQuery({
        queryKey: [QueryKeys.droplists, 'brands'],
        queryFn: () => droplistApi.get<DroplistBrands>('/brands').then((r) => r.data),
        retry: retryServerErrors,
    })

/** The brand's latest run; polls while the service is working on it. */
export const useDroplistRun = (brand: string) => {
    const queryClient = useQueryClient()
    const query = useQuery({
        queryKey: [QueryKeys.droplists, 'run', brand],
        queryFn: () => droplistApi.get<RunResponse>(`/brands/${brand}/run`).then((r) => r.data.run),
        retry: retryServerErrors,
        refetchInterval: (q) => (q.state.data?.busy ? 1500 : false),
    })

    // When an action finishes, refresh the brand list too (its tabs show which brands are busy).
    const busy = query.data?.busy ?? null
    const wasBusy = useRef(busy)
    useEffect(() => {
        if (wasBusy.current && !busy) queryClient.invalidateQueries({ queryKey: [QueryKeys.droplists, 'brands'] })
        wasBusy.current = busy
    }, [busy, queryClient])

    return query
}

// Every action answers with the updated run: put it straight in the cache so polling starts at once.
const useRunMutation = <TVars>(brand: string, fn: (vars: TVars) => Promise<RunResponse>) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: ({ run }) => {
            queryClient.setQueryData([QueryKeys.droplists, 'run', brand], run)
            queryClient.invalidateQueries({ queryKey: [QueryKeys.droplists, 'brands'] })
        },
    })
}

export const useStartParse = (brand: string) =>
    useRunMutation(brand, (config: DroplistConfig) =>
        droplistApi.post<RunResponse>(`/brands/${brand}/run`, { config }).then((r) => r.data)
    )

export const useSaveProducts = (brand: string) =>
    useRunMutation(brand, (body: { runId: string; products: DroplistProduct[] }) =>
        droplistApi.put<RunResponse>(`/brands/${brand}/run/products`, body).then((r) => r.data)
    )

export const usePushToV3 = (brand: string) =>
    useRunMutation(brand, (body: { runId: string; confirm: string }) =>
        droplistApi.post<RunResponse>(`/brands/${brand}/run/push`, body).then((r) => r.data)
    )

export const useCreateDroplistRelease = (brand: string) =>
    useRunMutation(brand, (body: { runId: string; config: DroplistConfig }) =>
        droplistApi.post<RunResponse>(`/brands/${brand}/run/release`, body).then((r) => r.data)
    )
